import type { IDBPDatabase } from 'idb';
import {
  type FontFamily,
  type UploadableFontProvider,
  checkFont,
  familyNameFromFileName,
} from '@burfy/docs-core';
import { type BurfyDocsDB, FONTS_STORE, openBurfyDocsDb } from '../storage/database.ts';
import { BUNDLED_FONTS } from './bundled-fonts.ts';

/**
 * Fonts available to this browser: the bundled families, plus whatever the
 * user has uploaded.
 *
 * Uploaded fonts are kept as bytes in IndexedDB and re-registered on every
 * load. Storing only the family name would leave a document referring to a
 * font that vanished on reload — the text would silently render in something
 * else, which is worse than an error because nobody would notice.
 */

export class FontUploadError extends Error {
  readonly reason: string;

  constructor(reason: string, message: string) {
    super(message);
    this.name = 'FontUploadError';
    this.reason = reason;
  }
}

export interface UploadedFontRecord {
  family: string;
  mime: string;
  bytes: Uint8Array;
  uploadedAt: string;
}

export class LocalFontProvider implements UploadableFontProvider {
  #db: Promise<IDBPDatabase<BurfyDocsDB>> | null = null;
  readonly #databaseName: string | undefined;
  /** Families already handed to the browser this session. */
  readonly #registered = new Set<string>();
  #restored: Promise<void> | null = null;

  constructor(options: { databaseName?: string } = {}) {
    this.#databaseName = options.databaseName;
  }

  #connection(): Promise<IDBPDatabase<BurfyDocsDB>> {
    this.#db ??= openBurfyDocsDb(this.#databaseName);
    return this.#db;
  }

  async close(): Promise<void> {
    const pending = this.#db;
    this.#db = null;
    if (pending) (await pending).close();
  }

  /** Register every stored font with the browser, once per session. */
  #restore(): Promise<void> {
    this.#restored ??= (async () => {
      try {
        const db = await this.#connection();
        const stored = await db.getAll(FONTS_STORE);
        await Promise.all(stored.map((record) => this.#register(record)));
      } catch (error) {
        // A document referring to an uploaded font will fall back to a system
        // one; that is a visible degradation, not a reason to fail to open.
        console.warn('[burfy-docs] could not restore uploaded fonts', error);
      }
    })();

    return this.#restored;
  }

  async #register(record: UploadedFontRecord): Promise<void> {
    if (this.#registered.has(record.family)) return;
    if (typeof FontFace === 'undefined') return;

    // The bytes are handed to the font engine directly. A URL is never
    // involved, so nothing is fetched and nothing can be pointed elsewhere.
    const face = new FontFace(record.family, record.bytes as BufferSource);
    await face.load();
    document.fonts.add(face);
    this.#registered.add(record.family);
  }

  async list(): Promise<FontFamily[]> {
    await this.#restore();

    const db = await this.#connection();
    const stored = await db.getAll(FONTS_STORE);
    const uploaded: FontFamily[] = stored
      .map((record) => ({
        family: record.family,
        label: record.family,
        source: 'uploaded' as const,
      }))
      .sort((a, b) => a.family.localeCompare(b.family));

    return [...BUNDLED_FONTS, ...uploaded];
  }

  async load(family: string): Promise<boolean> {
    await this.#restore();

    if (typeof document === 'undefined' || !document.fonts) return false;

    try {
      // Ask for the family at a representative size; the browser downloads the
      // face only now, which is why listing families costs nothing.
      const faces = await document.fonts.load(`12pt "${family}"`);
      return faces.length > 0 || document.fonts.check(`12pt "${family}"`);
    } catch {
      return false;
    }
  }

  async upload(bytes: Uint8Array, fileName: string): Promise<FontFamily> {
    const check = checkFont(bytes);
    if (!check.ok)
      throw new FontUploadError(check.rejection.reason, check.rejection.message);

    const family = familyNameFromFileName(fileName);
    const record: UploadedFontRecord = {
      family,
      mime: check.mime,
      bytes,
      uploadedAt: new Date().toISOString(),
    };

    try {
      // Register before storing: a font the browser rejects should not end up
      // in the database, where it would fail again on every future load.
      this.#registered.delete(family);
      await this.#register(record);
    } catch {
      throw new FontUploadError(
        'unreadable',
        'That font could not be read. It may be damaged or in an unsupported format.',
      );
    }

    const db = await this.#connection();
    await db.put(FONTS_STORE, record);

    return { family, label: family, source: 'uploaded' };
  }

  async remove(family: string): Promise<void> {
    const db = await this.#connection();
    await db.delete(FONTS_STORE, family);
    this.#registered.delete(family);
    // The face stays registered for this session: text already using it would
    // otherwise reflow the moment it is deleted, which is not what "remove
    // from the list" should do.
  }
}
